import React, { useState } from 'react';
import {
  Container, Card, CardContent, Typography, Button, Box,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, IconButton, Tabs, Tab
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import ViewListIcon from '@mui/icons-material/ViewList';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { API_URL } from '../../config';

// Helper: format date string without timezone conversion
const fmtDate = (val) => {
  if (!val) return '';
  const s = val.toString().substring(0, 10); // "YYYY-MM-DD"
  const [yr, mo, dy] = s.split('-');
  return `${dy}/${mo}/${yr}`;
};

// Orange header cell style (matches other reports)
const hdrCell = {
  fontWeight: 700,
  textAlign: 'center',
  py: { xs: 1.5, md: 2 },
  px: { xs: 1, md: 1.5 },
  color: '#ffffff',
  fontSize: { xs: '0.72rem', md: '0.85rem' },
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  borderRight: '1px solid rgba(255,255,255,0.2)',
  whiteSpace: 'nowrap',
  background: 'linear-gradient(135deg, #ffb74d 0%, #ffa726 100%)',
};

const dataCell = (extra = {}) => ({
  py: { xs: 1, md: 1.5 },
  px: { xs: 1, md: 1.5 },
  fontSize: { xs: '0.75rem', md: '0.85rem' },
  borderRight: '1px solid #e0e0e0',
  whiteSpace: 'nowrap',
  ...extra,
});

const ContainerListView = ({ onBackClick }) => {
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [data, setData]         = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  // ── Detail columns ──────────────────────────────────────────────────────────
  const detailHeaders = [
    { key: 'ContainerNo',               label: 'Container No' },
    { key: 'Received_at_factory_date',  label: 'Received at Factory' },
    { key: 'Shipping_Line',             label: 'Shipping Line' },
    { key: 'Booking_No',                label: 'Booking No' },
    { key: 'Gate_Out_Date',             label: 'Gate Out Date' },
    { key: 'TRANSITOR',                 label: 'Transitor' },
    { key: 'EmpotageDate',              label: 'Empotage Date' },
    { key: 'DomicileDate',              label: 'Domicile Date' },
    { key: 'INVOICE_NUMBER',            label: 'Invoice No' },
    { key: 'INVOICE_DATE',              label: 'Invoice Date' },
    { key: 'INVOICE_REF',               label: 'Invoice Ref' },
    { key: 'INVOICE_REF_DATE',          label: 'Invoice Ref Date' },
    { key: 'COO_NO',                    label: 'COO No' },
    { key: 'COO_RECEIVED_DATE',         label: 'COO Received' },
    { key: 'BAE_NO',                    label: 'BAE No' },
    { key: 'BAE_APPLIED_DATE',          label: 'BAE Applied' },
    { key: 'FIRST_BAE_RECEIVED_DATE',   label: 'First BAE Received' },
    { key: 'SEAL_NO',                   label: 'Seal No' },
  ];

  const dateKeys = new Set([
    'Received_at_factory_date', 'Gate_Out_Date', 'EmpotageDate', 'DomicileDate',
    'INVOICE_DATE', 'INVOICE_REF_DATE', 'COO_RECEIVED_DATE',
    'BAE_APPLIED_DATE', 'FIRST_BAE_RECEIVED_DATE',
  ]);

  // ── Excel download ──────────────────────────────────────────────────────────
  const handleExcelDownload = async () => {
    if (!data) return;
    const { addTitleRow, addSubtitleRow, addHeaderRow, addDataRow, addTotalRow, COLORS } = await import('../../utils/excelStyles');

    const wb = new ExcelJS.Workbook();
    wb.creator = 'Plywood Software Report';
    wb.created = new Date();
    const dateRange = `${fromDate.format('DD/MM/YYYY')} – ${toDate.format('DD/MM/YYYY')}`;
    const subtitle  = `Generated: ${new Date().toLocaleString()}   |   Date Range: ${dateRange}   |   Total: ${data.containers.length} containers`;

    // ── Sheet 1: Container Detail ──────────────────────────────────────────
    const ws1 = wb.addWorksheet('Container Detail', { views: [{ state: 'frozen', ySplit: 3 }] });
    const d1Cols = detailHeaders.length;
    ws1.columns = detailHeaders.map(({ label }) => ({
      key: label, width: Math.max(label.length + 4, 16)
    }));

    addTitleRow(ws1, 'CONTAINER LIST — DATE WISE', d1Cols);
    addSubtitleRow(ws1, subtitle, d1Cols);
    addHeaderRow(ws1, detailHeaders.map(h => h.label));

    data.containers.forEach((row, i) => {
      const values = detailHeaders.map(({ key }) =>
        dateKeys.has(key) ? fmtDate(row[key]) : (row[key] ?? '')
      );
      addDataRow(ws1, values, i, {
        boldCols: [1],
        textColor: '374151',
      });
    });
    ws1.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: d1Cols } };

    // ── Sheet 2: Summary by Shipping Line ─────────────────────────────────
    const ws2 = wb.addWorksheet('Summary by Shipping Line', { views: [{ state: 'frozen', ySplit: 3 }] });
    ws2.columns = [
      { key: 'line',  width: 28 },
      { key: 'count', width: 18 },
    ];
    addTitleRow(ws2, 'SUMMARY BY SHIPPING LINE', 2);
    addSubtitleRow(ws2, subtitle, 2);
    addHeaderRow(ws2, ['Shipping Line', 'Container Count']);

    data.summary.forEach((row, i) => {
      const isTotal = row.Shipping_Line === 'Grand Total';
      if (isTotal) {
        const tr = addTotalRow(ws2, [row.Shipping_Line, row.Container_Count]);
        tr.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
        tr.getCell(2).font = { bold: true, size: 13, color: { argb: 'A7F3D0' }, name: 'Calibri' };
      } else {
        const dr = addDataRow(ws2, [row.Shipping_Line, row.Container_Count], i, { boldCols: [1] });
        dr.getCell(2).alignment = { horizontal: 'center', vertical: 'middle' };
        dr.getCell(2).font = { bold: true, size: 11, color: { argb: COLORS.GREEN_DARK }, name: 'Calibri' };
      }
    });

    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/octet-stream' }),
      `Container_List_${fromDate.format('YYYY-MM-DD')}_to_${toDate.format('YYYY-MM-DD')}.xlsx`
    );
  };

  // ── Search ──────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await fetch(`${API_URL}/container/listbydate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          from_date: fromDate.format('YYYY-MM-DD'),
          to_date:   toDate.format('YYYY-MM-DD'),
        }),
      });
      if (!res.ok) throw new Error('API error: ' + res.statusText);
      const apiData = await res.json();

      const containers = apiData.data?.[0] || [];
      const summary    = apiData.data?.[1] || [];

      if (containers.length === 0) {
        setError('No containers found for this date range.');
        return;
      }
      setData({ containers, summary });
    } catch (e) {
      setError(e.message || 'Fetch failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Date picker shared props ────────────────────────────────────────────────
  const pickerSx = {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: '#fff',
      '&:hover fieldset': { borderColor: '#1b4332' },
      '&.Mui-focused fieldset': { borderColor: '#1b4332', borderWidth: '2px' },
    },
  };
  const popperSx = {
    '& .MuiPaper-root': { borderRadius: 2, boxShadow: '0 8px 24px rgba(27,67,50,0.15)' },
    '& .MuiPickersDay-root': {
      borderRadius: 1.5,
      '&.Mui-selected': { backgroundColor: '#1b4332', '&:hover': { backgroundColor: '#0f2419' } },
    },
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, pb: 4, px: { xs: 1, md: 2 } }}>

      {/* ── Search Card ── */}
      <Card sx={{
        mb: 4, borderRadius: 3,
        boxShadow: '0 4px 12px rgba(27,67,50,0.1)',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8faf9 100%)',
        border: '1px solid #e8f5e9'
      }}>
        <CardContent sx={{ p: { xs: 2, md: 4 } }}>
          <Box display="flex" alignItems="center" mb={3}>
            {onBackClick && (
              <IconButton onClick={onBackClick} sx={{ mr: 2, color: '#1b4332', '&:hover': { backgroundColor: '#f5f5f5' } }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box sx={{
              background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
              borderRadius: 2, p: 1.5, mr: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <ViewListIcon sx={{ fontSize: 28, color: '#ffffff' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1b4332', letterSpacing: '-0.5px' }}>
                Container List - Date Wise
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                Search containers by received-at-factory date range
              </Typography>
            </Box>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="stretch">
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{ textField: { fullWidth: true, sx: pickerSx }, popper: { sx: popperSx } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                slotProps={{ textField: { fullWidth: true, sx: pickerSx }, popper: { sx: popperSx } }}
              />
              <Button
                variant="contained"
                size="large"
                startIcon={<SearchIcon />}
                disabled={loading || !fromDate || !toDate}
                onClick={handleSearch}
                sx={{
                  minWidth: { xs: '100%', md: 160 },
                  borderRadius: 2, fontSize: '0.95rem', fontWeight: 600,
                  background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
                  color: 'white', textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(27,67,50,0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0f2419 0%, #1b4332 100%)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                Search
              </Button>
            </Box>
          </LocalizationProvider>
        </CardContent>
      </Card>

      {/* ── Results header ── */}
      {data && (
        <Card sx={{
          mb: 3, borderRadius: 3,
          background: 'linear-gradient(135deg, #1b4332 0%, #2d6a4f 100%)',
          boxShadow: '0 4px 12px rgba(27,67,50,0.2)',
        }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box sx={{ background: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 1.5, display: 'flex', alignItems: 'center' }}>
                  <ViewListIcon sx={{ fontSize: 32, color: '#ffffff' }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff' }}>
                    Containers Found
                  </Typography>
                  <Box display="flex" gap={1} alignItems="center" mt={0.5}>
                    <Chip
                      label={`${data.containers.length} container${data.containers.length !== 1 ? 's' : ''}`}
                      size="small"
                      sx={{ backgroundColor: 'rgba(255,255,255,0.9)', color: '#1b4332', fontWeight: 600 }}
                    />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                      {fromDate?.format('MMM DD, YYYY')} to {toDate?.format('MMM DD, YYYY')}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<DownloadIcon />}
                onClick={handleExcelDownload}
                sx={{
                  minWidth: { xs: '100%', md: 180 },
                  borderRadius: 2, fontSize: '0.95rem', fontWeight: 600,
                  background: '#ffffff', color: '#1b4332', textTransform: 'none',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': { background: '#f1f8f4', transform: 'translateY(-2px)' },
                  transition: 'all 0.3s ease',
                }}
              >
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Loading / Error ── */}
      {loading && (
        <Box textAlign="center" py={8}>
          <CircularProgress size={50} sx={{ color: '#1b4332' }} />
          <Typography variant="body1" sx={{ mt: 2, color: '#666', fontWeight: 500 }}>
            Searching containers...
          </Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 1 }}>{error}</Alert>}

      {/* ── Tabs + Tables ── */}
      {data && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #e8f5e9' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{
                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.95rem', color: '#666' },
                '& .MuiTab-root.Mui-selected': { color: '#1b4332' },
                '& .MuiTabs-indicator': { backgroundColor: '#1b4332', height: 3 },
              }}
            >
              <Tab label={`Container Detail (${data.containers.length})`} />
              <Tab label={`Summary by Shipping Line (${data.summary.length})`} />
            </Tabs>
          </Box>

          {/* Tab 0 — Detail */}
          {activeTab === 0 && (
            <>
              <Typography variant="caption" sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', color: '#666', py: 1, fontStyle: 'italic' }}>
                ← Swipe left to see all columns →
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <TableContainer>
                  <Table sx={{ minWidth: 1400 }} size="small">
                    <TableHead>
                      <TableRow>
                        {detailHeaders.map(({ label }) => (
                          <TableCell key={label} sx={hdrCell}>{label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.containers.map((row, i) => (
                        <TableRow key={i} sx={{
                          backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8faf9',
                          '&:hover': { backgroundColor: '#e8f5e9' },
                          transition: 'background 0.15s',
                        }}>
                          {detailHeaders.map(({ key }) => (
                            <TableCell key={key} sx={dataCell({
                              fontWeight: key === 'ContainerNo' ? 600 : 400,
                              color: key === 'ContainerNo' ? '#1b4332' : '#444',
                            })}>
                              {dateKeys.has(key) ? fmtDate(row[key]) : (row[key] ?? '—')}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}

          {/* Tab 1 — Summary */}
          {activeTab === 1 && (
            <TableContainer component={Paper} sx={{ borderRadius: 0 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...hdrCell, textAlign: 'left' }}>Shipping Line</TableCell>
                    <TableCell sx={hdrCell}>Container Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.summary.map((row, i) => {
                    const isTotal = row.Shipping_Line === 'Grand Total';
                    return (
                      <TableRow key={i} sx={{
                        backgroundColor: isTotal ? '#1b4332' : (i % 2 === 0 ? '#ffffff' : '#f8faf9'),
                        '&:hover': { backgroundColor: isTotal ? '#0f2419' : '#e8f5e9' },
                      }}>
                        <TableCell sx={dataCell({ fontWeight: isTotal ? 700 : 500, color: isTotal ? '#ffffff' : '#333' })}>
                          {row.Shipping_Line}
                        </TableCell>
                        <TableCell sx={dataCell({ textAlign: 'center', fontWeight: isTotal ? 800 : 600, color: isTotal ? '#a7f3d0' : '#1b4332', fontSize: isTotal ? '1rem' : '0.9rem' })}>
                          {row.Container_Count}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {/* ── Empty state ── */}
      {!data && !loading && !error && (
        <Card sx={{
          p: 8, textAlign: 'center', borderRadius: 3,
          background: 'linear-gradient(135deg, #f8faf9 0%, #ffffff 100%)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          border: '2px dashed #d0e8d5'
        }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
            borderRadius: '50%', width: 100, height: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', mb: 3
          }}>
            <CalendarTodayIcon sx={{ fontSize: 50, color: '#1b4332' }} />
          </Box>
          <Typography variant="h5" sx={{ color: '#1b4332', fontWeight: 700, mb: 1 }}>
            No Containers to Display
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mt: 1 }}>
            Select a date range above and click Search to view containers
          </Typography>
        </Card>
      )}
    </Container>
  );
};

export default ContainerListView;
